// 금천아 빌려줘 — 화면 표기 단일 소스.
//
// ⚠️ 금액·날짜 포맷을 컴포넌트마다 따로 만들면 목록과 상세가 다르게 보인다
//    ("20000.00원" vs "20,000원"이 실제로 그렇게 생겼다 — 2026-09-29 수정).
//    표기를 바꿀 일이 생기면 이 파일만 고친다.

/** 금액 → "20,000원". 소수점은 버리고(원 단위) 천 단위 콤마를 넣는다. */
export function formatWon(value) {
  if (value === null || value === undefined || value === "") return "-";
  const number = Number(value);
  if (Number.isNaN(number)) return "-";
  return `${Math.round(number).toLocaleString("ko-KR")}원`;
}

/** 결제금액 변동 괄호 표기 — 서버 payment_change → "최초 85,000 · 환불 20,000".
 *  변동이 없으면(null) 빈 문자열. 목록 카드와 상세가 같은 문구를 쓴다. */
export function formatPaymentChange(change) {
  if (!change) return "";
  const num = (v) => Math.round(Number(v)).toLocaleString("ko-KR");
  const kind = change.kind === "extra" ? "추가" : "환불";
  return `최초 ${num(change.initial)} · ${kind} ${num(change.amount)}`;
}

/** 날짜(YYYY-MM-DD 또는 ISO 일시) → "9/30". 올해가 아니면 "25.9/30".
 *
 * ⚠️ 연도를 붙이는 이유: 카드에서 M/D만 보면 작년·내년 건을 올해로 읽는다.
 *    올해 건은 대부분이므로 평소에는 짧게 두고, 다른 해일 때만 앞에 YY.를 붙인다.
 */
export function formatDateShort(value) {
  if (!value) return "-";
  const text = String(value);
  // 날짜만 오는 값(YYYY-MM-DD)과 ISO 일시 모두 받는다.
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!match) return text;
  const [, year, month, day] = match;
  const short = `${Number(month)}/${Number(day)}`;
  const thisYear = new Date().getFullYear();
  return Number(year) === thisYear ? short : `${year.slice(2)}.${short}`;
}

/** ISO 일시 → "2026-09-29 17:05". */
export function formatDateTime(value) {
  if (!value) return "-";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "-";
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())} ${pad(
    dt.getHours()
  )}:${pad(dt.getMinutes())}`;
}

/** 마지막 동기화 시각 → "9/29 17:05 동기화". */
export function formatSyncedAt(value) {
  if (!value) return "동기화 기록 없음";
  const dt = new Date(value);
  if (Number.isNaN(dt.getTime())) return "동기화 기록 없음";
  const pad = (n) => String(n).padStart(2, "0");
  return `${dt.getMonth() + 1}/${dt.getDate()} ${pad(dt.getHours())}:${pad(
    dt.getMinutes()
  )} 동기화`;
}

/** 일정 라벨 — 직접 수령·반납 주문은 "방문 수령 / 방문 반납", 그 외는 "배송 / 회수".
 *  판정(fulfillment)은 서버(services/options.py)가 한다. 목록·상세가 같은 문구를 쓴다. */
export function scheduleLabels(order) {
  if (order?.fulfillment === "direct") {
    return { out: "방문 수령", back: "방문 반납" };
  }
  return { out: "배송", back: "회수" };
}

/** 기준일 라벨 — 전집(월대여형)은 "대여 시작일", 그 외(백일상)는 "행사일".
 *  유형(rental_kind)은 서버가 상품 설정(RentalProduct)으로 판정한다. */
export const MONTHLY = "MONTHLY";
export function eventDateLabel(order) {
  return order?.rental_kind === MONTHLY ? "대여 시작일" : "행사일";
}

/** 환불액 내역 — 직접 수령·반납으로 배송·회수비가 더해졌을 때만
 *  "보증금 20,000 + 배송·회수비 10,000 − 차감 5,000". 아니면 빈 문자열.
 *  더해진 배송·회수비는 저장된 값(환불액 + 차감액 − 보증금)에서 역산한다 —
 *  설정이 나중에 바뀌어도 그때 실제로 적용된 금액이 보인다. */
export function formatRefundBreakdown(order) {
  const num = (v) => Math.round(Number(v)).toLocaleString("ko-KR");
  const refund = Number(order?.refund_amount);
  const deposit = Number(order?.deposit || 0);
  const deduction = Number(order?.deduction_amount || 0);
  if (Number.isNaN(refund)) return "";
  const fee = refund + deduction - deposit;
  if (!(fee > 0)) return "";
  return `보증금 ${num(deposit)} + 배송·회수비 ${num(fee)}${
    deduction > 0 ? ` − 차감 ${num(deduction)}` : ""
  }`;
}

/** 수령 완료 버튼 문구 — 지난 시도가 중간에 멈췄거나 일부 단계가 끝났으면 "이어서".
 *  진행 판정(handover.steps·error)은 서버(services/status.handover_progress)가 준다. */
export function handoverLabel(order) {
  const steps = order?.handover?.steps || [];
  const started = order?.handover?.error || steps.some((step) => step.done);
  return started ? "수령 완료 (이어서)" : "수령 완료";
}

// 보증금 환불 안내 — 결제수단(서버 payment_method: 카페24 코드)에 따라 처리 방법이 다르다(2026-10-15).
//   카드: 카페24에서 부분취소(카드 부분취소는 결제일로부터 12개월 안에만 된다)
//   무통장입금: 고객 계좌로 직접 이체한 뒤 카페24에서 환불완료 처리
//   그 밖(간편결제 등): 결제수단 이름만 알려주고 사람이 판단한다
export function refundGuide(order, amount) {
  const won = formatWon(amount);
  if (order.payment_method === "card") {
    return `카페24에서 ${won} 부분취소 (결제일로부터 12개월 안)`;
  }
  if (order.payment_method === "cash") {
    return `계좌 이체 ${won} + 카페24 환불완료 처리`;
  }
  const name = order.payment_method_name || "결제수단 확인 필요";
  return `${name} 결제 — 카페24에서 결제수단에 맞게 ${won} 환불 처리`;
}
